export const CHECKLIST_ITEMS = [
    'WASTE_ACTIVE',
    'ADR_COMPLETE',
    'CAP_VALID',
    'DESTINATION_AUTHORIZED',
    'TRANSPORTER_AUTHORIZED',
    'TRANSPORTER_ADR_EVIDENCE',
    'BSD_PRINTED',
    'LABELS_PRINTED',
    'DECLARATION_PRINTED',
    'CONSIGNE_PRINTED',
    'PRODUCER_SIGNATURE',
];
/**
 * Items a site manager may exempt with a reason. The legal blocks stay blocks: a hazardous waste
 * without a valid CAP or to an unauthorized installation, and an incomplete ADR block
 * (BR-07, BR-08 with loi 28-00 art. 74, BR-11).
 */
const EXEMPTABLE = [
    'TRANSPORTER_AUTHORIZED',
    'BSD_PRINTED',
    'LABELS_PRINTED',
    'DECLARATION_PRINTED',
    'CONSIGNE_PRINTED',
    // the site may fall back to the paper copies for one chargement (FR-FIA-18)
    'PRODUCER_SIGNATURE',
];
export function evaluateChecklist(f) {
    const adr = f.lines.some((l) => l.adrIdentified);
    const failing = (test) => f.lines.filter((l) => !test(l)).map((l) => l.bsdNumber ?? l.expeditionId);
    const item = (key, status, lines = []) => ({
        key,
        status,
        exemptable: EXEMPTABLE.includes(key) && status === 'BLOCK',
        lines,
    });
    const hazardousFail = (test) => {
        const bad = f.lines.filter((l) => !test(l));
        if (bad.length === 0)
            return 'PASS';
        return bad.some((l) => l.hazardous) ? 'BLOCK' : 'WARN';
    };
    const items = [
        item('WASTE_ACTIVE', failing((l) => l.wasteActive).length ? 'BLOCK' : 'PASS', failing((l) => l.wasteActive)),
        item('ADR_COMPLETE', adr
            ? failing((l) => !l.adrIdentified || l.adrComplete).length
                ? 'BLOCK'
                : 'PASS'
            : 'NOT_APPLICABLE', failing((l) => !l.adrIdentified || l.adrComplete)),
        item('CAP_VALID', f.lines.some((l) => l.hazardous)
            ? hazardousFail((l) => !l.hazardous || l.capValidOnDate)
            : 'NOT_APPLICABLE', failing((l) => !l.hazardous || l.capValidOnDate)),
        item('DESTINATION_AUTHORIZED', hazardousFail((l) => l.destinationAuthorizedOnDate && l.drCodePerformed), failing((l) => l.destinationAuthorizedOnDate && l.drCodePerformed)),
        item('TRANSPORTER_AUTHORIZED', f.transporterAuthorizedOnDate
            ? 'PASS'
            : f.transporterExempted
                ? 'WARN'
                : f.lines.some((l) => l.hazardous)
                    ? 'BLOCK'
                    : 'WARN'),
        // BR-11: a missing or expired evidence warns, it does not stop the truck (FR-FIA-05)
        {
            ...item('TRANSPORTER_ADR_EVIDENCE', !adr ? 'NOT_APPLICABLE' : f.transporterAdrEvidenceGaps.length ? 'WARN' : 'PASS'),
            ...(adr && f.transporterAdrEvidenceGaps.length
                ? { evidence: f.transporterAdrEvidenceGaps }
                : {}),
        },
        item('BSD_PRINTED', failing((l) => l.bsdPrinted).length ? 'BLOCK' : 'PASS', failing((l) => l.bsdPrinted)),
        item('LABELS_PRINTED', f.lines.some((l) => l.hazardous)
            ? hazardousFail((l) => !l.hazardous || l.labelsPrinted)
            : 'NOT_APPLICABLE', failing((l) => !l.hazardous || l.labelsPrinted)),
        item('DECLARATION_PRINTED', adr ? (f.declarationPrinted ? 'PASS' : 'BLOCK') : 'NOT_APPLICABLE'),
        {
            ...item('CONSIGNE_PRINTED', !adr || f.regime1136Claimed ? 'NOT_APPLICABLE' : f.consignePrinted ? 'PASS' : 'BLOCK'),
            ...(f.consigneLanguage ? { params: { language: f.consigneLanguage } } : {}),
        },
        // FR-FIA-19: in the in-app signature mode the expéditeur signs the issue before the pickup
        item('PRODUCER_SIGNATURE', !f.signatureRequired
            ? 'NOT_APPLICABLE'
            : failing((l) => l.signedByProducer).length
                ? 'BLOCK'
                : 'PASS', f.signatureRequired ? failing((l) => l.signedByProducer) : []),
    ];
    // A blocking item a site manager exempted with a reason becomes a warning (FR-FIA-23)
    return items.map((i) => {
        const e = f.exemptions.find((x) => x.item === i.key && x.reason.trim().length >= 10);
        return e && i.exemptable ? { ...i, status: 'WARN', exemptedReason: e.reason } : i;
    });
}
export function checklistBlocks(items) {
    return items.some((i) => i.status === 'BLOCK');
}
/**
 * Holders of the BSD copies (décret 2-14-85 art. 15: five copies). The ministry model is awaited
 * (OQ-01); until then the distribution is a tenant setting and this is the default.
 */
export const DEFAULT_COPY_HOLDERS = [
    'Exemplaire 1 — Expéditeur (conservé au départ)',
    'Exemplaire 2 — Transporteur',
    'Exemplaire 3 — Destinataire',
    'Exemplaire 4 — Retour à l’expéditeur après réception',
    'Exemplaire 5 — Retour à l’expéditeur après élimination',
];
/** The default holders in the document's language (FR-FIA-17), the copy number beyond five. */
const COPY_HOLDERS = {
    fr: { holders: DEFAULT_COPY_HOLDERS, other: (n) => `Exemplaire ${n}` },
    ar: {
        holders: [
            'النسخة 1 — المرسل (تُحفظ عند الانطلاق)',
            'النسخة 2 — الناقل',
            'النسخة 3 — المستلم',
            'النسخة 4 — تُعاد إلى المرسل بعد الاستلام',
            'النسخة 5 — تُعاد إلى المرسل بعد التخلص',
        ],
        other: (n) => `النسخة ${n}`,
    },
    en: {
        holders: [
            'Copy 1 — Consignor (kept at departure)',
            'Copy 2 — Carrier',
            'Copy 3 — Consignee',
            'Copy 4 — Back to the consignor after reception',
            'Copy 5 — Back to the consignor after disposal',
        ],
        other: (n) => `Copy ${n}`,
    },
};
/** The default holder of a copy, from 0, in a language. */
export function defaultCopyHolder(index, locale = 'fr') {
    const d = COPY_HOLDERS[locale];
    return d.holders[index] ?? d.other(index + 1);
}
/**
 * The holder named on each copy. A holder the site wrote prints as written; one left as the
 * French default wording (which the site preferences used to save) is a default, printed in the
 * document's language.
 */
export function copyHolders(copies, configured, locale = 'fr') {
    return Array.from({ length: copies }, (_, i) => {
        const holder = configured?.[i]?.trim();
        return holder && holder !== defaultCopyHolder(i) ? holder : defaultCopyHolder(i, locale);
    });
}
//# sourceMappingURL=checklist.js.map